<template>
  <div ref="rootEl" class="chart-v5 relative w-full" :class="[overrideHeight || 'h-[468px]']">
    <div v-if="showExport" class="flex justify-end">
      <ExportMenu class="absolute z-10 top-2 right-2" :exports="[{ type: 'chart', action: downloadSVG }]" />
    </div>

    <div v-if="isEmpty" class="chart-v5__empty">
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
        <rect x="6" y="28" width="8" height="14" rx="2" fill="#E8E9E9" />
        <rect x="20" y="18" width="8" height="24" rx="2" fill="#E8E9E9" />
        <rect x="34" y="8" width="8" height="34" rx="2" fill="#E8E9E9" />
      </svg>
      <span>No data available</span>
    </div>

    <svg v-else ref="svgEl" :width="svgWidth" :height="svgHeight">
      <text v-if="chart_config.title"
        :x="svgWidth / 2" :y="28" text-anchor="middle"
        class="chart-v5__title">
        {{ chart_config.title }}
      </text>
      <text v-if="chart_config.subtitle"
        :x="svgWidth / 2" :y="48" text-anchor="middle"
        class="chart-v5__subtitle">
        {{ chart_config.subtitle }}
      </text>

      <g :transform="mainTransform">
        <!-- Grid lines -->
        <line v-for="(gl, i) in gridLines" :key="'grid-' + i"
          :x1="gl.x1" :y1="gl.y1" :x2="gl.x2" :y2="gl.y2"
          class="chart-v5__grid-line" />

        <!-- Axes -->
        <g ref="xAxisEl" :transform="`translate(0,${innerHeight})`" class="chart-v5__axis" />
        <g ref="yAxisLeftEl" class="chart-v5__axis" />
        <g v-if="hasRightAxis" ref="yAxisRightEl" :transform="`translate(${innerWidth},0)`" class="chart-v5__axis" />

        <!-- Zero baseline (diverging charts only) -->
        <line v-if="zeroLineY !== null"
          :x1="0" :y1="zeroLineY" :x2="innerWidth" :y2="zeroLineY"
          class="chart-v5__zero-line" />

        <!-- Bars -->
        <rect v-for="bar in barGeometry" :key="bar.key"
          :x="bar.x" :y="bar.y" :width="bar.width" :height="bar.height"
          :fill="bar.fill" :stroke="bar.stroke" stroke-width="1"
          :rx="bar.rx" :ry="bar.ry"
          :class="['chart-v5__bar', {
            'chart-v5__bar--dimmed': hoveredGroup !== null && hoveredGroup !== bar.groupKey,
            'chart-v5__bar--active': hoveredGroup === bar.groupKey,
          }]"
          @mouseenter="onBarEnter($event, bar)"
          @mousemove="onMouseMove"
          @mouseleave="onLeave"
          @click="$emit('bar-click', bar.data)" />

        <!-- Lines -->
        <template v-for="line in lineGeometry" :key="line.key">
          <path :d="line.pathD" fill="none" :stroke="line.color"
            :stroke-width="line.strokeWidth || 2.5"
            :stroke-dasharray="line.strokeDasharray || null"
            stroke-linecap="round" stroke-linejoin="round"
            :class="['chart-v5__line', {
              'chart-v5__line--dimmed': hoveredGroup !== null && hoveredGroup !== line.key,
            }]" />
          <template v-if="!line.hidePoints">
            <circle v-for="pt in line.points" :key="pt.key"
              :cx="pt.cx" :cy="pt.cy"
              :fill="hoveredKey === pt.key ? pt.color : 'white'"
              :stroke="pt.color" stroke-width="2"
              :r="hoveredKey === pt.key ? 6 : 3.5"
              :class="['chart-v5__point', {
                'chart-v5__point--dimmed': hoveredGroup !== null && hoveredGroup !== line.key,
              }]"
              @mouseenter="onPointEnter($event, pt, line.key)"
              @mousemove="onMouseMove"
              @mouseleave="onLeave"
              @click="$emit('line-click', pt.data)" />
          </template>
        </template>

        <!-- Data labels -->
        <text v-for="lbl in dataLabelGeometry" :key="lbl.key"
          :x="lbl.x" :y="lbl.y"
          :text-anchor="lbl.anchor" :dominant-baseline="lbl.baseline"
          class="chart-v5__data-label">
          {{ lbl.text }}
        </text>

        <!-- Goal target line -->
        <template v-if="goalLineY !== null">
          <line :x1="0" :y1="goalLineY" :x2="innerWidth" :y2="goalLineY"
            class="chart-v5__goal-line" />
          <rect :x="innerWidth + 4" :y="goalLineY - 9" width="30" height="18" rx="3"
            fill="#FEF2F2" stroke="#E8615A" stroke-width="0.5" />
          <text :x="innerWidth + 19" :y="goalLineY" dy=".35em" text-anchor="middle"
            class="chart-v5__goal-label">Goal</text>
        </template>
      </g>

      <!-- Legend -->
      <g v-if="showLegend" :transform="legendGroupTransform">
        <rect :x="legendBox.x" :y="legendBox.y"
          :width="legendBox.width" :height="legendBox.height"
          rx="4" fill="white" stroke="#E5E7EB" stroke-width="1" />
        <g v-for="(item, i) in legendItems" :key="'legend-' + i" :transform="item.transform"
          class="chart-v5__legend-item"
          @mouseenter="onLegendEnter(item)"
          @mouseleave="onLegendLeave">
          <rect :width="12" :height="12" :fill="solidColor(item.color)" rx="3" y="1" />
          <text x="18" y="7" dy=".35em" text-anchor="start" class="chart-v5__legend-text">
            {{ item.label }}
          </text>
        </g>
      </g>
    </svg>

    <Teleport to="body">
      <transition name="chart-tooltip">
        <div v-if="tooltip.visible" class="chart-v5__tooltip" :style="tooltipStyle">
          <div class="chart-v5__tooltip-pip" :style="{ backgroundColor: tooltip.color }" />
          <div class="chart-v5__tooltip-body" v-html="tooltip.content" />
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script>
import * as d3 from 'd3'
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue'
import { isDemoMode, spoofText } from '@/utils/demoSpoof'

const DEFAULT_COLORS = [
  '#2563EB', '#059669', '#D97706', '#DC2626',
  '#7C3AED', '#EA580C', '#0284C7', '#DB2777',
]

export default {
  name: 'ChartV5',
  components: { ExportMenu },
  props: {
    overrideHeight: { type: String, required: false },
    showExport: { type: Boolean, default: true },
    chart_data: {
      type: Object,
      required: true,
      default() {
        return {
          series: [{ values: [0], config: { label: 'Default Series', backgroundColor: 'rgba(192,0,0,0.4)', type: 'bar', yAxis: 'left', offset: 0 } }],
          labels: ['Default'],
        }
      },
    },
    chart_config: {
      type: Object,
      default() {
        return {
          chartType: 'vertical_bar',
          margin: { top: 80, right: 60, bottom: 60, left: 80 },
          maxBarWidth: 10,
          xAxis: { labelRotation: -45, tickFormat: null, fontSize: 12 },
          yAxes: [
            { position: 'left', tickFormat: null, fontSize: 12, maxHorizontalLabelLength: 40 },
            { position: 'right', tickFormat: null, fontSize: 12 },
          ],
          legend: { display: true, position: 'top', spacing: 15 },
          labelDisplayConfig: { maxLabels: 40, minLabelSpacing: 5, labelFontSize: 12 },
          formatting: { percentage: true, sig_figs: 1 },
          title: '',
          subtitle: '',
        }
      },
    },
  },
  emits: ['bar-click', 'line-click'],

  data() {
    return {
      containerWidth: 0,
      containerHeight: 0,
      hoveredKey: null,
      hoveredGroup: null,
      tooltip: { visible: false, content: '', color: '', x: 0, y: 0 },
      animationReady: false,
      resizeObserver: null,
      resizeTimer: null,
      measureCtx: null,
      visibleTickIndices: null,
    }
  },

  computed: {
    isEmpty() {
      return !this.chart_data?.series?.length || !this.chart_data?.labels?.length
    },
    isHorizontal() {
      return this.chart_config?.chartType === 'horizontal_bar'
    },
    processedData() {
      if (this.isEmpty) return []
      const demo = isDemoMode()
      return this.chart_data.labels.map((label, index) => {
        // Category labels are painted as SVG axis ticks + tooltips the DOM spoof
        // engine can't reach. In Demo Mode, spoof a label only when it maps to an
        // entity (object_ids present) -- those are practice/provider/entity names.
        // Date/metric-bucket categories (no object_ids) are left intact so PHI-free
        // charts aren't corrupted with fake names.
        const hasEntity = this.chart_data.object_ids?.[index] != null
        const dp = {
          label: demo && hasEntity ? spoofText(label) : label,
          index,
        }
        this.chart_data.series.forEach(s => {
          const v = s.values[index]
          dp[s.config.label] = Number.isFinite(v) ? v : null
        })
        if (this.chart_data.colors?.[index]) dp.color = this.chart_data.colors[index]
        const exc = this.chart_data.filter_exclusions?.[index]
        dp.excludeFromFilter = exc === 1 || exc === true
        if (this.chart_data.object_ids?.[index]) dp.object_ids = this.chart_data.object_ids[index]
        return dp
      })
    },
    barSeries() {
      return this.chart_data.series.filter(s => s.config.type !== 'line')
    },
    lineSeries() {
      return this.chart_data.series.filter(s => s.config.type === 'line')
    },

    barSeriesByGroup() {
      const groups = {}
      this.chart_data.series.forEach((s, idx) => {
        if (s.config.type === 'line') return
        const key = `${s.config.offset || 0}-${s.config.yAxis || 'left'}`
        if (!groups[key]) groups[key] = []
        groups[key].push({ series: s, idx })
      })
      return groups
    },
    maxOffset() {
      if (!this.barSeries.length) return 1
      return Math.max(...this.barSeries.map(s => s.config.offset || 0)) + 1
    },

    xAxisFontSize() { return this.chart_config.xAxis?.fontSize || 12 },
    yAxisFontSize() { return this.chart_config.yAxes?.[0]?.fontSize || 12 },

    effectiveMargin() {
      // Reserve top space only when there's an in-chart title/subtitle; reclaim
      // it otherwise so the plot fills the card (the panel header carries the
      // title in embedded contexts). Reserve extra bottom/left for axis titles.
      const hasTitle = !!(this.chart_config?.title || this.chart_config?.subtitle)
      const hasXTitle = !!this.chart_config?.xAxis?.title
      const hasYTitle = (this.chart_config?.yAxes || []).some(a => a.title)
      const base = {
        top: hasTitle ? 80 : 24,
        right: 24,
        bottom: 40 + (hasXTitle ? 22 : 0),
        left: 52 + (hasYTitle ? 22 : 0),
      }
      const m = { ...base, ...this.chart_config?.margin }
      if (!this.processedData.length || !this.containerWidth) return m

      if (this.showLegend && this.chart_config.legend?.position === 'subtitle') {
        m.top += (this.chart_config.legend.spacing || 15)
      }
      if (this.showLegend && this.chart_config.legend?.position === 'right') {
        // MEASURE ONLY WHAT THE LEGEND DRAWS. hideFromLegend series are not
        // rendered, so measuring them reserved width for text that never
        // appears — and on a diverging chart the hidden half carries the LONGER
        // label ("P0 Critical closed" vs "P0 Critical").
        const shown = this.chart_data.series.filter(s => !s.config.hideFromLegend)
        const maxW = Math.max(0, ...shown.map(s => this.measureText(s.config.label, 12)))
        // ADD to the caller's margin rather than replacing it. This assigned,
        // so an explicit margin.right was silently discarded — and because the
        // RIGHT AXIS renders at translate(innerWidth,0), a legend band wider
        // than the caller expected pushed the axis under the legend text. That
        // is why a right-axis line appeared to have no axis at all: the scale
        // existed, the ticks were drawn, and they were painted underneath the
        // legend outside the visible plot.
        m.right = Math.max(m.right, maxW + 18 + 6 + 40)
      }

      if (this.isHorizontal) {
        const maxW = Math.max(0, ...this.processedData.map(d => this.measureText(String(d.label), this.yAxisFontSize)))
        m.left = Math.min(maxW * 1.5, this.containerWidth * 0.6)
      } else {
        const rotation = this.chart_config.xAxis?.labelRotation || 0
        if (rotation !== 0) {
          const maxW = Math.max(0, ...this.processedData.map(d => this.measureText(String(d.label), this.xAxisFontSize)))
          const radians = Math.abs(rotation) * (Math.PI / 180)
          const projected = maxW * Math.sin(radians) + this.xAxisFontSize * Math.cos(radians)
          m.bottom = Math.max(m.bottom, projected - 40)
        }
      }
      return m
    },

    innerWidth() { return Math.max(this.containerWidth - this.effectiveMargin.left - this.effectiveMargin.right, 0) },
    innerHeight() { return Math.max(this.containerHeight - this.effectiveMargin.top - this.effectiveMargin.bottom, 0) },
    svgWidth() { return this.containerWidth },
    svgHeight() { return this.containerHeight },
    mainTransform() { return `translate(${this.effectiveMargin.left},${this.effectiveMargin.top})` },

    scales() {
      const data = this.processedData
      const w = this.innerWidth
      const h = this.innerHeight
      if (!data.length || !w || !h) return null

      const yAxesCfg = this.chart_config.yAxes || []
      let yMaxLeft = yAxesCfg[0]?.max ?? 0
      let yMaxRight = yAxesCfg[1]?.max ?? 0
      // Tracks how far BELOW zero the data goes; stays 0 for all-positive data,
      // so the domain is unchanged for every existing chart.
      let yMinLeftData = 0

      Object.entries(this.barSeriesByGroup).forEach(([key, group]) => {
        const yAxis = key.split('-')[1]
        // SUM EACH SIGN SEPARATELY, matching the per-sign stacking below. A
        // single sum lets up and down cancel, so a week of +58/-8 measures as
        // 50 and the domain is too small for either bar.
        const stackedMax = d3.max(data, d => {
          let sum = 0
          group.forEach(({ series }) => {
            const v = d[series.config.label]
            if (v !== null && !isNaN(v) && v > 0) sum += v
          })
          return sum
        }) || 0
        const stackedMin = d3.min(data, d => {
          let sum = 0
          group.forEach(({ series }) => {
            const v = d[series.config.label]
            if (v !== null && !isNaN(v) && v < 0) sum += v
          })
          return sum
        }) || 0
        if (yAxis === 'left') {
          yMaxLeft = Math.max(yMaxLeft, stackedMax)
          yMinLeftData = Math.min(yMinLeftData, stackedMin)
        } else yMaxRight = Math.max(yMaxRight, stackedMax)
      })

      this.lineSeries.forEach(s => {
        const yAxis = s.config.yAxis || 'left'
        const mx = d3.max(data, d => d[s.config.label]) || 0
        if (yAxis === 'left') yMaxLeft = Math.max(yMaxLeft, mx)
        else yMaxRight = Math.max(yMaxRight, mx)
      })

      let xScale, yScales = {}

      if (this.isHorizontal) {
        if (this.chart_data.goal_target) yMaxLeft = Math.max(yMaxLeft, this.chart_data.goal_target)
        yMaxLeft = yMaxLeft > 0 ? yMaxLeft : 1
        xScale = d3.scaleLinear().domain([0, yMaxLeft]).nice().range([0, w])
        yScales.left = d3.scaleBand().domain(d3.range(data.length)).range([0, h]).padding(0.15)
        if (yMaxRight > 1) {
          const rightCfgH = yAxesCfg.find(a => a.position === 'right') || {}
          yScales.right = d3.scaleLinear()
            .domain([rightCfgH.min ?? 0, yMaxRight]).nice().range([h, 0])
        }
      } else {
        xScale = d3.scaleBand().domain(data.map((_, i) => i)).range([0, w]).padding(0.2)
        if (this.chart_config.maxBarWidth) {
          const bw = xScale.bandwidth()
          if (bw > this.chart_config.maxBarWidth) {
            const idealPad = 1 - (this.chart_config.maxBarWidth * data.length / w)
            xScale.padding(Math.min(Math.max(idealPad, 0), 0.9))
          }
        }
        if (this.chart_data.goal_target) yMaxLeft = Math.max(yMaxLeft, this.chart_data.goal_target)
        yMaxLeft = yMaxLeft > 0 ? yMaxLeft : 1
        yMaxRight = yMaxRight > 0 ? yMaxRight : 1
        const leftCfg = yAxesCfg.find(a => a.position === "left") || {}
        // An explicit min still wins; otherwise use the data's own negative
        // extent (0 when there is none).
        const yMinLeft = leftCfg.min ?? Math.min(0, yMinLeftData)
        yScales.left = d3.scaleLinear().domain([yMinLeft, yMaxLeft]).nice().range([h, 0])
        if (yMaxRight > 1) {
          // HONOURS yAxes[1].min, like the left axis already does. This was
          // hardcoded to 0, so a right-axis line whose values never approach
          // zero (a cumulative backlog sitting at 32-193) was squashed into the
          // top of the plot and drawn straight through the bars.
          const rightCfg = yAxesCfg.find(a => a.position === 'right') || {}
          const yMinRight = rightCfg.min ?? 0
          yScales.right = d3.scaleLinear()
            .domain([yMinRight, yMaxRight]).nice().range([h, 0])
        }
      }
      return { xScale, yScales }
    },
    hasRightAxis() { return !!this.scales?.yScales?.right },

    barGeometry() {
      if (!this.scales) return []
      const { xScale, yScales } = this.scales
      const bars = []
      const bandwidth = (this.isHorizontal ? yScales.left.bandwidth() : xScale.bandwidth()) / this.maxOffset

      Object.entries(this.barSeriesByGroup).forEach(([groupKey, group]) => {
        const [offsetStr, yAxis] = groupKey.split('-')
        const offset = parseInt(offsetStr, 10)
        const scale = yScales[yAxis] || yScales.left

        this.processedData.forEach((d, di) => {
          // SEPARATE ACCUMULATORS PER SIGN. One shared `cumulative` makes a
          // negative value continue from the top of the positive stack, so a
          // diverging chart draws its downward bars hanging off the upward ones
          // instead of from the zero line. Positives stack up from 0, negatives
          // stack down from 0.
          let cumulativeUp = 0
          let cumulativeDown = 0
          const seriesInGroup = group.length
          group.forEach(({ series, idx }, si) => {
            const v = d[series.config.label]
            if (v === null || isNaN(v)) return
            const start = v < 0 ? cumulativeDown : cumulativeUp
            const end = start + v
            if (v < 0) cumulativeDown = end
            else cumulativeUp = end

            const rawFill = d.color || series.config.backgroundColor || DEFAULT_COLORS[idx % DEFAULT_COLORS.length]
            const fill = this.boostOpacity(rawFill, 0.85)
            const stroke = this.solidColor(rawFill)
            const isTopOfStack = si === seriesInGroup - 1 || group.slice(si + 1).every(({ series: s }) => {
              const sv = d[s.config.label]
              return sv === null || isNaN(sv) || sv === 0
            })
            let x, y, w, h

            if (this.isHorizontal) {
              x = xScale(start)
              y = yScales.left(di) + offset * bandwidth
              w = xScale(end) - xScale(start)
              h = bandwidth - 2
            } else {
              x = xScale(di) + offset * bandwidth
              // SIGNED-SAFE GEOMETRY. Previously this was
              //   y = scale(end); h = min(scale(start), innerHeight) - scale(end)
              // which yields a NEGATIVE height whenever end sits below start —
              // i.e. any negative value — and an SVG rect with negative height
              // silently does not render. That is why a diverging stacked bar
              // (inflow up, outflow down) appeared to draw nothing at all.
              //
              // Taking the top as min(a, b) and the height as |a - b| is
              // sign-agnostic. VERIFIED IDENTICAL to the old formula for
              // all-positive stacked data, so every existing chart is unchanged;
              // set yAxes[].min below 0 to use the negative half.
              const startY = scale(start)
              const endY = scale(end)
              const clampedStartY = Math.min(startY, this.innerHeight)
              y = this.animationReady ? Math.min(clampedStartY, endY) : clampedStartY
              w = Math.max(bandwidth - (this.chart_config.barGap || 2), 0)
              h = this.animationReady ? Math.abs(clampedStartY - endY) : 0
            }

            bars.push({
              key: `bar-${idx}-${di}`,
              groupKey: `bar-group-${di}`,
              x, y, width: w, height: h, fill, stroke,
              rx: isTopOfStack && !this.isHorizontal ? 3 : (this.isHorizontal && isTopOfStack ? 3 : 0),
              ry: isTopOfStack && !this.isHorizontal ? 3 : (this.isHorizontal && isTopOfStack ? 3 : 0),
              roundTop: isTopOfStack,
              data: d,
              seriesLabel: series.config.label,
              seriesConfig: series.config,
              _start: start, _end: end,
            })
          })
        })
      })
      return bars
    },

    lineGeometry() {
      if (!this.scales) return []
      const { xScale, yScales } = this.scales
      return this.lineSeries.map((series, li) => {
        const offset = series.config.offset || 0
        const yAxis = series.config.yAxis || 'left'
        // SILENT FALLBACK, DELIBERATELY LOUD NOW. A series asking for the right
        // axis when no right scale exists gets drawn on the LEFT one — at which
        // point a backlog of 193 renders against a [-200,200] diverging domain
        // and reads as a mirrored line through the bars. It looks like a scaling
        // bug; it is a missing axis. Warn once so the next person sees it.
        if (!yScales[yAxis] && yAxis !== 'left') {
          // eslint-disable-next-line no-console
          console.warn(`Chart_V5: series "${series.config.label}" wants the `
            + `${yAxis} axis but no ${yAxis} scale exists — falling back to `
            + 'left. Check that its values exceed 1 and that yAxes declares it.')
        }
        const scale = yScales[yAxis] || yScales.left
        const color = series.config.backgroundColor || DEFAULT_COLORS[li % DEFAULT_COLORS.length]

        const lineGen = d3.line()
          .defined(d => d[series.config.label] !== null)
          .x((d, i) => this.isHorizontal
            ? xScale(d[series.config.label])
            : xScale(i) + xScale.bandwidth() * (offset + 0.5))
          .y(d => this.isHorizontal
            ? yScales.left(d.index) + yScales.left.bandwidth() / 2
            : scale(d[series.config.label]))
          .curve(d3.curveMonotoneX)

        const points = this.processedData
          .filter(d => d[series.config.label] !== null)
          .map(d => ({
            key: `pt-${li}-${d.index}`,
            cx: this.isHorizontal
              ? xScale(d[series.config.label])
              : xScale(d.index) + xScale.bandwidth() * (offset + 0.5),
            cy: this.isHorizontal
              ? yScales.left(d.index) + yScales.left.bandwidth() / 2
              : scale(d[series.config.label]),
            color,
            data: d,
            seriesLabel: series.config.label,
            seriesConfig: series.config,
          }))

        return {
          key: `line-${li}`,
          pathD: lineGen(this.processedData),
          color,
          points,
          strokeDasharray: series.config.strokeDasharray || null,
          strokeWidth: series.config.strokeWidth || 2.5,
          hidePoints: series.config.hidePoints || false,
        }
      })
    },

    dataLabelGeometry() {
      // Opt-out, mirroring hideGridLines. Side-by-side bar groups put labels a
      // bandwidth apart, so on a dense series two adjacent numbers run together and
      // read as one wrong number ("83" and "24" render as "8324").
      if (!this.scales || !this.animationReady || this.chart_config.hideDataLabels) return []
      const { xScale, yScales } = this.scales
      const labels = []
      const fontSize = 11
      const visibleIndices = this.visibleTickIndices

      Object.entries(this.barSeriesByGroup).forEach(([key, group]) => {
        const [offsetStr, yAxis] = key.split('-')
        const offset = parseInt(offsetStr, 10)
        const scale = yScales[yAxis] || yScales.left
        const bandwidth = (this.isHorizontal ? yScales.left.bandwidth() : xScale.bandwidth()) / this.maxOffset

        this.processedData.forEach((d, di) => {
          if (visibleIndices && !visibleIndices.has(di)) return
          // Same per-sign accumulators as the bar geometry above — the label
          // positions must follow the bars or they detach on a diverging chart.
          let cumulativeUp = 0
          let cumulativeDown = 0
          group.forEach(({ series, idx }) => {
            const v = d[series.config.label]
            if (v === null || isNaN(v)) return
            const start = v < 0 ? cumulativeDown : cumulativeUp
            const end = start + v
            if (v < 0) cumulativeDown = end
            else cumulativeUp = end
            const formatted = this.formatDataValue(v)

            if (this.isHorizontal) {
              const barWidth = xScale(end) - xScale(start)
              const textW = this.measureText(formatted, fontSize)
              const inside = barWidth > textW + 10
              labels.push({
                key: `lbl-${idx}-${di}`,
                x: inside ? xScale(start) + barWidth / 2 : xScale(end) + 5,
                y: yScales.left(di) + bandwidth / 2 + offset * bandwidth,
                anchor: inside ? 'middle' : 'start',
                baseline: 'middle',
                text: formatted,
              })
            } else {
              const barH = scale(start) - scale(end)
              const barTop = scale(end)
              const textH = fontSize + 2
              if (barH <= 8) return
              const inside = barH > textH + 10
              labels.push({
                key: `lbl-${idx}-${di}`,
                x: xScale(di) + xScale.bandwidth() * ((offset + 0.5) / this.maxOffset),
                y: inside ? barTop + barH / 2 : barTop - 6,
                anchor: 'middle',
                baseline: inside ? 'middle' : 'auto',
                text: formatted,
              })
            }
          })
        })
      })
      return labels
    },

    gridLines() {
      if (!this.scales || this.chart_config.hideGridLines) return []
      const { yScales, xScale } = this.scales
      if (this.isHorizontal) {
        if (!xScale.ticks) return []
        return xScale.ticks().map(t => ({ x1: xScale(t), y1: 0, x2: xScale(t), y2: this.innerHeight }))
      }
      const scale = yScales.left
      if (!scale?.ticks) return []
      // Skip zero itself — either the axis edge (all-positive) or the dedicated
      // zero line (diverging) provides that reference. Negative ticks DO get
      // gridlines; filtering on `t > 0` left the lower half of a diverging
      // chart with none.
      return scale.ticks().filter(t => t !== 0).map(t => ({ x1: 0, y1: scale(t), x2: this.innerWidth, y2: scale(t) }))
    },

    // A ZERO BASELINE, only when the domain crosses zero. The x-axis is pinned to
    // innerHeight (the bottom), which IS the zero line on an all-positive chart —
    // but on a diverging one zero sits in the middle and the downward bars have
    // nothing to hang from. Returns null otherwise, so no existing chart gains a
    // line it did not have.
    zeroLineY() {
      // RESPECTS hideGridLines. PerformanceTrajectory is already a diverging
      // chart (min/max straddle zero) and sets hideGridLines to draw a
      // deliberately bare chart — it must not silently gain a baseline.
      if (this.chart_config.hideGridLines) return null
      const scale = this.scales?.yScales?.left
      if (!scale || this.isHorizontal) return null
      const [lo, hi] = scale.domain()
      if (lo >= 0 || hi <= 0) return null
      return scale(0)
    },

    goalLineY() {
      if (!this.chart_data.goal_target || !this.scales?.yScales?.left) return null
      return this.scales.yScales.left(this.chart_data.goal_target)
    },

    showLegend() {
      return this.chart_config.legend?.display && this.chart_data.series.length > 1
    },
    legendItems() {
      if (!this.showLegend) return []
      const pos = this.chart_config.legend?.position || 'top'
      // OPT-IN: series.config.hideFromLegend drops a series from the legend
      // while still drawing it. Needed for a diverging chart, where the up and
      // down halves of one group are two series but one legend entry — the bar's
      // position already says which is which. Absent on every existing consumer,
      // so the filter is a no-op for them.
      // Carries the ORIGINAL index: seriesKey is `bar-series-${i}` and must
      // match the drawn series, so a filtered index would break hover highlight.
      const series = this.chart_data.series
        .map((s, originalIndex) => ({ ...s, originalIndex }))
        .filter(s => !s.config.hideFromLegend)

      if (pos === 'right') {
        return series.map((s, i) => ({
          transform: `translate(0,${i * 24})`,
          color: s.config.backgroundColor || DEFAULT_COLORS[i % DEFAULT_COLORS.length],
          label: s.config.label,
          seriesKey: s.config.type === 'line' ? `line-${this.lineSeries.indexOf(s)}` : `bar-series-${s.originalIndex}`,
        }))
      }
      if (pos === 'subtitle') {
        let xOff = 0
        const items = series.map((s, i) => {
          const color = s.config.backgroundColor || DEFAULT_COLORS[i % DEFAULT_COLORS.length]
          const tw = this.measureText(s.config.label, 12) + 28
          const item = { transform: `translate(${xOff},0)`, color, label: s.config.label, _width: tw,
            seriesKey: s.config.type === 'line' ? `line-${this.lineSeries.indexOf(s)}` : `bar-series-${s.originalIndex}` }
          xOff += tw
          return item
        })
        const shift = xOff / 2
        return items.map(it => ({
          ...it,
          transform: `translate(${parseFloat(it.transform.match(/-?[\d.]+/)[0]) - shift},0)`,
        }))
      }
      return series.map((s, i) => ({
        transform: `translate(0,${i * 22})`,
        color: s.config.backgroundColor || DEFAULT_COLORS[i % DEFAULT_COLORS.length],
        label: s.config.label,
        seriesKey: s.config.type === 'line' ? `line-${this.lineSeries.indexOf(s)}` : `bar-series-${s.originalIndex}`,
      }))
    },
    legendBox() {
      if (!this.showLegend || !this.legendItems.length) return { x: 0, y: 0, width: 0, height: 0 }
      const pos = this.chart_config.legend?.position || 'top'
      const pad = 8

      if (pos === 'subtitle') {
        // Horizontal layout - calculate total width from items
        let totalW = 0
        this.legendItems.forEach(it => {
          const xOff = parseFloat(it.transform.match(/-?[\d.]+/)[0])
          const labelW = this.measureText(it.label, 12)
          totalW = Math.max(totalW, xOff + 18 + labelW)
        })
        const minX = parseFloat(this.legendItems[0].transform.match(/-?[\d.]+/)[0])
        return { x: minX - pad, y: -6, width: totalW - minX + pad * 2, height: 26 }
      }

      // Vertical layout (top, right, left)
      const maxLabelW = Math.max(0, ...this.legendItems.map(it => this.measureText(it.label, 12)))
      const itemH = pos === 'right' ? 24 : 22
      const totalH = this.legendItems.length * itemH
      return { x: -pad, y: -6, width: 18 + maxLabelW + pad * 2, height: totalH + pad }
    },
    legendGroupTransform() {
      const pos = this.chart_config.legend?.position || 'top'
      if (pos === 'right') return `translate(${this.effectiveMargin.left + this.innerWidth + 20},${this.effectiveMargin.top})`
      if (pos === 'subtitle') return `translate(${this.svgWidth / 2},${this.chart_config.title ? 52 : 20})`
      return `translate(${this.effectiveMargin.left + 16},${this.effectiveMargin.top})`
    },

    tooltipStyle() {
      return {
        position: 'fixed',
        top: `${this.tooltip.y - 10}px`,
        left: `${this.tooltip.x + 15}px`,
        zIndex: 9999,
        pointerEvents: 'none',
      }
    },

    axisKey() {
      return JSON.stringify([
        this.scales ? 'yes' : 'no',
        this.innerWidth, this.innerHeight,
        this.chart_config.xAxis, this.chart_config.yAxes,
        this.processedData.length,
      ])
    },
  },

  watch: {
    axisKey() {
      this.$nextTick(() => this.renderAxes())
    },
    chart_data: {
      handler() { this.$nextTick(() => this.renderAxes()) },
      deep: true,
    },
    chart_config: {
      handler() { this.$nextTick(() => this.renderAxes()) },
      deep: true,
    },
  },

  mounted() {
    this.containerWidth = this.$el.clientWidth
    this.containerHeight = this.$el.clientHeight
    this.setupResizeObserver()
    this.$nextTick(() => {
      this.renderAxes()
      requestAnimationFrame(() => { this.animationReady = true })
    })
  },

  beforeUnmount() {
    if (this.resizeObserver) this.resizeObserver.disconnect()
    if (this.resizeTimer) clearTimeout(this.resizeTimer)
  },

  methods: {
    measureText(text, fontSize) {
      if (!this.measureCtx) {
        this.measureCtx = document.createElement('canvas').getContext('2d')
      }
      this.measureCtx.font = `${fontSize}px "IBM Plex Sans", sans-serif`
      return this.measureCtx.measureText(String(text)).width
    },

    formatDataValue(value) {
      if (value === null || value === undefined || isNaN(value)) return 'N/A'
      let v = value
      if (this.chart_config.formatting?.currency) {
        return '$' + v.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })
      }
      if (this.chart_config.formatting?.percentage) v *= 100
      v = v.toFixed(this.chart_config.formatting?.sig_figs ?? 1)
      if (this.chart_config.formatting?.percentage) v += '%'
      return v
    },

    truncateLabel(label, maxLen) {
      return label.length > maxLen ? label.slice(0, maxLen) + '\u2026' : label
    },

    // Boost rgba opacity so transparent consumer colors don't look washed out
    boostOpacity(color, minOpacity) {
      const c = d3.color(color)
      if (!c) return color
      if (c.opacity < minOpacity) c.opacity = minOpacity
      return c.toString()
    },

    // Return fully opaque version of a color for strokes
    solidColor(color) {
      const c = d3.color(color)
      if (!c) return '#000000'
      c.opacity = 1
      return c.toString()
    },

    setupResizeObserver() {
      this.resizeObserver = new ResizeObserver(entries => {
        const { width, height } = entries[0].contentRect
        if (Math.abs(width - this.containerWidth) < 3 && Math.abs(height - this.containerHeight) < 3) return
        if (this.resizeTimer) clearTimeout(this.resizeTimer)
        this.resizeTimer = setTimeout(() => {
          this.containerWidth = width
          this.containerHeight = height
        }, 150)
      })
      this.resizeObserver.observe(this.$refs.rootEl)
    },

    renderAxes() {
      if (!this.scales || !this.$refs.xAxisEl) return
      const { xScale, yScales } = this.scales
      const data = this.processedData
      const yAxesCfg = this.chart_config.yAxes || []
      const font = '"IBM Plex Sans", sans-serif'

      const xAxisG = d3.select(this.$refs.xAxisEl)
      xAxisG.selectAll('*').remove()

      if (this.isHorizontal) {
        const ticks = this.getAdaptiveContinuousTicks(xScale, this.innerWidth, this.xAxisFontSize)
        xAxisG.call(
          d3.axisBottom(xScale).tickValues(ticks).tickSize(6).tickPadding(8)
            .tickFormat(this.chart_config.xAxis?.tickFormat || (d => this.formatDataValue(d)))
        )
        this.visibleTickIndices = new Set(yScales.left.domain())
      } else {
        const ticks = this.getAdaptiveBandTicks(data, this.innerWidth)
        xAxisG.call(
          d3.axisBottom(xScale).tickValues(ticks).tickSize(6).tickPadding(8)
            .tickFormat(d => {
              const label = data[d]?.label || ''
              return this.chart_config.xAxis?.tickFormat ? this.chart_config.xAxis.tickFormat(label) : label
            })
        )
        xAxisG.selectAll('text')
          .attr('transform', `rotate(${this.chart_config.xAxis?.labelRotation || 0})`)
          .style('text-anchor', this.chart_config.xAxis?.labelRotation ? 'end' : 'middle')
        this.visibleTickIndices = new Set(ticks)
      }
      xAxisG.selectAll('text').style('font-size', `${this.xAxisFontSize}px`).style('font-family', font)
      xAxisG.selectAll('.tick line').style('stroke', '#9CA3AF')
      xAxisG.select('.domain').style('stroke', '#9CA3AF')

      if (yScales.left && this.$refs.yAxisLeftEl) {
        const yLeftG = d3.select(this.$refs.yAxisLeftEl)
        yLeftG.selectAll('*').remove()
        if (this.isHorizontal) {
          const maxLabelLen = Math.floor(this.effectiveMargin.left / 7)
          yLeftG.call(
            d3.axisLeft(yScales.left).tickSize(6).tickPadding(8)
              .tickFormat(d => {
                const label = data[d]?.label || ''
                const fmt = yAxesCfg[0]?.tickFormat ? yAxesCfg[0].tickFormat(label) : label
                return this.truncateLabel(fmt, maxLabelLen)
              })
          )
        } else {
          const leftCfg = yAxesCfg.find(a => a.position === 'left')
          const ticks = leftCfg?.tickValues || this.getAdaptiveContinuousTicks(yScales.left, this.innerHeight, leftCfg?.fontSize || 12)
          yLeftG.call(
            d3.axisLeft(yScales.left).tickValues(ticks).tickSize(6).tickPadding(8)
              .tickFormat(leftCfg?.tickFormat || (d => this.formatDataValue(d)))
          )
        }
        yLeftG.selectAll('text').style('font-size', `${this.yAxisFontSize}px`).style('font-family', font)
        yLeftG.selectAll('.tick line').style('stroke', '#9CA3AF')
        yLeftG.select('.domain').style('stroke', '#9CA3AF')
      }

      if (yScales.right && this.$refs.yAxisRightEl) {
        const yRightG = d3.select(this.$refs.yAxisRightEl)
        yRightG.selectAll('*').remove()
        const rightCfg = yAxesCfg.find(a => a.position === 'right')
        const ticks = this.getAdaptiveContinuousTicks(yScales.right, this.innerHeight, rightCfg?.fontSize || 12)
        yRightG.call(
          d3.axisRight(yScales.right).tickValues(ticks).tickSize(6).tickPadding(8)
            .tickFormat(rightCfg?.tickFormat || (d => this.formatDataValue(d)))
        )
        yRightG.selectAll('text').style('font-size', `${rightCfg?.fontSize || 12}px`).style('font-family', font)
        yRightG.selectAll('.tick line').style('stroke', '#9CA3AF')
        yRightG.select('.domain').style('stroke', '#9CA3AF')
      }

      // Axis titles (driven by chart_config.xAxis.title / yAxes[].title).
      this.renderAxisTitles(font)
    },

    renderAxisTitles(font) {
      const titleStyle = sel => sel
        .style('font-size', '12px')
        .style('font-family', font)
        .style('font-weight', '500')
        .style('fill', '#6B7280')
        .style('text-anchor', 'middle')

      const xTitle = this.chart_config?.xAxis?.title
      const xG = d3.select(this.$refs.xAxisEl)
      xG.selectAll('.chart-v5__axis-title').remove()
      if (xTitle) {
        titleStyle(
          xG.append('text')
            .attr('class', 'chart-v5__axis-title')
            .attr('x', this.innerWidth / 2)
            .attr('y', 40)
        ).text(xTitle)
      }

      const leftCfg = (this.chart_config?.yAxes || []).find(a => a.position === 'left') || (this.chart_config?.yAxes || [])[0]
      const yTitle = leftCfg?.title
      const yG = d3.select(this.$refs.yAxisLeftEl)
      yG.selectAll('.chart-v5__axis-title').remove()
      if (yTitle && !this.isHorizontal) {
        titleStyle(
          yG.append('text')
            .attr('class', 'chart-v5__axis-title')
            .attr('transform', 'rotate(-90)')
            .attr('x', -this.innerHeight / 2)
            .attr('y', -this.effectiveMargin.left + 16)
        ).text(yTitle)
      }
    },

    getAdaptiveBandTicks(data, availableSpace) {
      const fontSize = this.xAxisFontSize
      const rotation = this.chart_config.xAxis?.labelRotation || 0
      const minSpacing = this.chart_config.labelDisplayConfig?.minLabelSpacing || 5
      const maxLabelSize = Math.max(0, ...data.map(d => {
        const w = this.measureText(String(d.label), fontSize)
        if (rotation === 0) return w
        const rad = Math.abs(rotation) * (Math.PI / 180)
        return w * Math.cos(rad) + fontSize * Math.sin(rad)
      }))
      const effectiveSpacing = Math.max(minSpacing, 15)
      const estimatedSize = maxLabelSize + effectiveSpacing
      const maxLabels = Math.max(1, Math.floor(availableSpace / estimatedSize))
      if (maxLabels >= data.length) return d3.range(data.length)
      const step = Math.ceil(data.length / maxLabels)
      return d3.range(0, data.length, step)
    },

    getAdaptiveContinuousTicks(scale, axisLength, fontSize) {
      const minSpacing = (this.chart_config.labelDisplayConfig?.minLabelSpacing || 5) * 4
      const maxTicks = Math.max(2, Math.floor(axisLength / (fontSize + minSpacing)))
      let ticks = scale.ticks()
      if (ticks.length > maxTicks) {
        const step = Math.ceil(ticks.length / maxTicks)
        ticks = ticks.filter((_, i) => i % step === 0)
      }
      return ticks
    },

    onBarEnter(event, bar) {
      this.hoveredKey = bar.key
      this.hoveredGroup = bar.groupKey
      const content = bar.seriesConfig.tooltip?.formatter
        ? bar.seriesConfig.tooltip.formatter(bar.data)
        : `<strong>${bar.seriesLabel}</strong><br/>${bar.data.label}: ${this.formatDataValue(bar.data[bar.seriesLabel])}`
      this.tooltip = { visible: true, content, color: bar.fill, x: event.clientX, y: event.clientY }
    },
    onPointEnter(event, pt, lineKey) {
      this.hoveredKey = pt.key
      this.hoveredGroup = lineKey
      const content = pt.seriesConfig.tooltip?.formatter
        ? pt.seriesConfig.tooltip.formatter(pt.data)
        : `<strong>${pt.seriesLabel}</strong><br/>${pt.data.label}: ${this.formatDataValue(pt.data[pt.seriesLabel])}`
      this.tooltip = { visible: true, content, color: pt.color, x: event.clientX, y: event.clientY }
    },
    onMouseMove(event) {
      this.tooltip.x = event.clientX
      this.tooltip.y = event.clientY
    },
    onLeave() {
      this.hoveredKey = null
      this.hoveredGroup = null
      this.tooltip.visible = false
    },
    onLegendEnter(item) {
      this.hoveredGroup = item.seriesKey
    },
    onLegendLeave() {
      this.hoveredGroup = null
    },

    downloadSVG() {
      const el = this.$refs.svgEl
      if (!el) return
      const serializer = new XMLSerializer()
      let source = serializer.serializeToString(el)
      if (!source.match(/^<svg[^>]+xmlns="http:\/\/www\.w3\.org\/2000\/svg"/)) {
        source = source.replace(/^<svg/, '<svg xmlns="http://www.w3.org/2000/svg"')
      }
      const blob = new Blob([source], { type: 'image/svg+xml;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'chart.svg'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    },
  },
}
</script>

<style scoped>
/* === AXES === */
.chart-v5__axis :deep(.tick text) {
  fill: #6B7280;
  font-weight: 600;
  font-feature-settings: 'tnum' 1;
}

/* === GRID === */
.chart-v5__grid-line {
  stroke: #E5E7EB;
  stroke-width: 1;
}

/* Zero baseline on a diverging chart — darker than a gridline because it is a
   reference line, matching the axis stroke (#9CA3AF) used in drawAxes. */
.chart-v5__zero-line {
  stroke: #9CA3AF;
  stroke-width: 1;
}

/* === TITLE === */
.chart-v5__title {
  font-size: 17px;
  font-weight: 600;
  font-family: 'IBM Plex Sans', sans-serif;
  fill: #111827;
  letter-spacing: -0.025em;
}
.chart-v5__subtitle {
  font-size: 12px;
  font-family: 'IBM Plex Sans', sans-serif;
  fill: #6B7280;
  font-weight: 400;
  letter-spacing: 0.01em;
}

/* === BARS === */
.chart-v5__bar {
  transition: opacity 0.2s ease, filter 0.2s ease,
    height 0.4s ease-out, y 0.4s ease-out,
    width 0.4s ease-out, x 0.4s ease-out;
  cursor: pointer;
}
.chart-v5__bar--dimmed {
  opacity: 0.2;
  filter: saturate(0.4);
}
.chart-v5__bar--active {
  filter: brightness(1.04) saturate(1.15);
}

/* === LINES === */
.chart-v5__line {
  pointer-events: none;
  transition: opacity 0.25s ease;
}
.chart-v5__line--dimmed {
  opacity: 0.12;
}
.chart-v5__point {
  cursor: pointer;
  transition: r 0.2s ease, opacity 0.25s ease, fill 0.15s ease;
}
.chart-v5__point--dimmed {
  opacity: 0.15;
}

/* === DATA LABELS === */
.chart-v5__data-label {
  font-size: 12px;
  font-weight: 600;
  font-family: 'IBM Plex Sans', sans-serif;
  fill: #1F2937;
  paint-order: stroke;
  stroke: white;
  stroke-width: 3px;
  pointer-events: none;
  font-feature-settings: 'tnum' 1;
}

/* === GOAL LINE === */
.chart-v5__goal-line {
  stroke: #DC6B62;
  stroke-width: 1.5;
  stroke-dasharray: 6 4;
  opacity: 0.6;
}
.chart-v5__goal-label {
  font-size: 9px;
  font-family: 'IBM Plex Sans', sans-serif;
  fill: #DC6B62;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

/* === LEGEND === */
.chart-v5__legend-item {
  cursor: default;
  transition: opacity 0.2s ease;
}
.chart-v5__legend-item:hover {
  opacity: 0.8;
}
.chart-v5__legend-text {
  font-size: 11px;
  font-weight: 500;
  font-family: 'IBM Plex Sans', sans-serif;
  fill: #4B5563;
}

/* === EMPTY STATE === */
.chart-v5__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 12px;
  color: #6B7280;
  font-size: 13px;
  font-family: 'IBM Plex Sans', sans-serif;
}

/* === TOOLTIP === */
.chart-v5__tooltip {
  display: flex;
  align-items: stretch;
  gap: 10px;
  background: rgba(255, 255, 255, 0.97);
  backdrop-filter: blur(16px) saturate(1.8);
  -webkit-backdrop-filter: blur(16px) saturate(1.8);
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 12px;
  font-family: 'IBM Plex Sans', sans-serif;
  box-shadow:
    0 0 0 0.5px rgba(0, 0, 0, 0.03),
    0 4px 16px rgba(0, 0, 0, 0.06),
    0 12px 40px rgba(0, 0, 0, 0.04);
  max-width: 300px;
  line-height: 1.5;
  color: #1F2937;
  pointer-events: none;
  font-feature-settings: 'tnum' 1;
}
.chart-v5__tooltip-pip {
  width: 3px;
  border-radius: 1.5px;
  flex-shrink: 0;
}
.chart-v5__tooltip-body :deep(strong) {
  font-weight: 600;
  color: #111827;
}
.chart-tooltip-enter-active,
.chart-tooltip-leave-active {
  transition: opacity 0.1s ease;
}
.chart-tooltip-enter-from,
.chart-tooltip-leave-to {
  opacity: 0;
}
</style>
